import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { translatePath, wordsPath } from "../../utils/routes";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftRightIcon, BookmarkCheckIcon, BrainIcon, CopyIcon, Loader2Icon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { languages } from "../../data/languages";
import { PageHeader } from "../../components/app/PageHeader";
import { Toggle } from "../../components/ui/Toggle";
import { Tag } from "../../components/ui/Tag";
import { SpeakButton } from "../../components/games/SpeakButton";
import { translate, TranslationResult } from "../../utils/translate";
import { courseTitle, getLanguage } from "../../utils/language";
import { spring } from "../../utils/motion";
const suggestions = ["How much does it cost?", "Where is the train station?", "I would like a coffee", "The bill, please"];
interface Lookup {
  id: string;
  source: string;
  result: string;
  from: string;
  to: string;
}
export function Translate() {
  const {
    courses
  } = useLearner();
  const {
    saveWord,
    isSaved,
    words
  } = useWords();
  const reduce = useReducedMotion() ?? false;
  const params = useParams();
  const navigate = useNavigate();
  const from = params.from ?? "en";
  const to = params.to ?? "es";
  // The language direction lives in the URL (/app/translate/en/es) so it can be shared and bookmarked.
  const go = (f: string, t: string) => navigate(translatePath(f, t), {
    replace: true
  });
  const setFrom = (v: string) => go(v, to);
  const setTo = (v: string) => go(from, v);
  const [input, setInput] = useState("");
  const [result, setResult] = useState<TranslationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [swapTurns, setSwapTurns] = useState(0);
  const [recent, setRecent] = useState<Lookup[]>([{
    id: "r1",
    source: "the receipt",
    result: "el recibo",
    from: "en",
    to: "es"
  }, {
    id: "r2",
    source: "Is it included?",
    result: "¿Está incluido?",
    from: "en",
    to: "es"
  }, {
    id: "r3",
    source: "la cola",
    result: "the queue",
    from: "es",
    to: "en"
  }]);

  // Which course do saves go into? The one teaching the "foreign" side from the other side.
  const course = useMemo(() => courses.find(c => c.target === to && c.base === from || c.target === from && c.base === to), [courses, from, to]);
  const targetSide = course ? course.target : to;
  useEffect(() => {
    if (!input.trim()) {
      setResult(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = window.setTimeout(() => {
      setResult(translate(input, from, to));
      setLoading(false);
    }, 450);
    return () => window.clearTimeout(t);
  }, [input, from, to]);
  const termAndMeaning = (source: string, translated: string) => targetSide === to ? {
    term: translated,
    translation: source
  } : {
    term: source,
    translation: translated
  };
  const commit = () => {
    if (!result || !result.supported || !result.text) return;
    setRecent(r => [{
      id: `r-${Date.now()}`,
      source: input.trim(),
      result: result.text,
      from,
      to
    }, ...r].slice(0, 8));
    if (!remember) return;
    if (!course) {
      toast("Not saved", {
        description: `Add a ${getLanguage(to).name} course to remember lookups.`
      });
      return;
    }
    const pair = termAndMeaning(input.trim(), result.text);
    const {
      created
    } = saveWord({
      ...pair,
      courseId: course.id,
      source: "translator"
    });
    toast.success(created ? "Remembered" : "Already in My Words", {
      description: created ? `“${pair.term}” will show up in your ${courseTitle(course)} lessons.` : undefined
    });
  };
  const saveToken = (source: string, target: string) => {
    if (!course) return;
    const pair = termAndMeaning(source, target);
    const {
      created
    } = saveWord({
      ...pair,
      courseId: course.id,
      source: "translator"
    });
    if (created) toast.success(`“${pair.term}” saved`);
  };
  const swap = () => {
    go(to, from);
    setSwapTurns(n => n + 1);
    if (result?.text) setInput(result.text);
  };
  const langSelect = (value: string, onChange: (v: string) => void, label: string) => <label className="min-w-0 flex-1" data-id="element-1612" data-mp="2oxn1w3fhm0l9">
      <span className="sr-only" data-id="element-1613" data-mp="2193tzp0cnptp">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="focus-ring w-full cursor-pointer appearance-none truncate rounded-xl border-3 border-line bg-surface px-3.5 py-2.5 text-[15px] font-bold text-ink" data-id="element-1614" data-mp="217spfmz2ye1j">
        {languages.map(l => <option key={l.code} value={l.code} data-id="element-1615" data-mp="266wwa6yj7zop">
            {l.name}
          </option>)}
      </select>
    </label>;
  const translatorWords = words.filter(w => w.source === "translator");
  const usedInLessons = translatorWords.filter(w => w.usedIn > 0).length;
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1616" data-mp="3p6cxgcl0sjy3">
      <PageHeader title="Translate" description="Look anything up. What you translate is remembered and quietly woven into your lessons." data-id="element-1617" data-mp="3tzfu4tl3g9ii" />

      <div className="mt-6 flex items-center gap-2 md:gap-3" data-id="element-1618" data-mp="3k8kw6ryhjlh8">
        {langSelect(from, setFrom, "Translate from")}
        <motion.button type="button" onClick={swap} aria-label="Swap languages" animate={{
        rotate: swapTurns * 180
      }} whileTap={{
        scale: 0.92
      }} transition={spring.bouncy} className="focus-ring grid h-11 w-11 shrink-0 place-items-center rounded-xl border-3 border-line bg-accent text-night shadow-brutal-sm" data-id="element-1619" data-mp="bs9mjpcsgbwh">
          <ArrowLeftRightIcon className="h-5 w-5" strokeWidth={2.5} data-id="element-1620" data-mp="2nika50pi3j3m" />
        </motion.button>
        {langSelect(to, setTo, "Translate to")}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-1621" data-mp="2rlgwn2i5vzcu">
        <div className="relative rounded-card border-3 border-line bg-surface shadow-brutal" data-id="element-1622" data-mp="y1q2azy1235v">
          <label htmlFor="tr-input" className="sr-only" data-id="element-1623" data-mp="t1k7jawgo0jk">
            Text to translate
          </label>
          <textarea id="tr-input" value={input} onChange={e => setInput(e.target.value)} onBlur={commit} onKeyDown={e => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            commit();
          }
        }} rows={5} placeholder={`Type in ${getLanguage(from).name}…`} className="block min-h-[176px] w-full resize-none rounded-card bg-transparent p-5 pr-12 text-xl font-medium text-ink placeholder:text-ink-2/60 outline-none" data-id="element-1624" data-mp="oavehr54uvib" />
          {input && <button type="button" onClick={() => setInput("")} aria-label="Clear" className="focus-ring absolute right-3 top-3 rounded-lg p-1.5 text-ink-2 hover:text-ink" data-id="element-1625" data-mp="1zp90srk8n2w8">
              <XIcon className="h-5 w-5" data-id="element-1626" data-mp="236n9niin0s8k" />
            </button>}
          <div className="flex items-center justify-between border-t-3 border-line px-4 py-3" data-id="element-1627" data-mp="278i7gd1d0nhl">
            <span className="text-xs text-ink-2" data-id="element-1628" data-mp="2c1be56ge25wb">{input.length} / 500</span>
            <SpeakButton text={input || " "} lang={getLanguage(from).speech} data-id="element-1629" data-mp="2gytdq68in8rn" />
          </div>
        </div>

        <div className="relative flex flex-col rounded-card border-3 border-line bg-brand-tint shadow-brutal" aria-live="polite" data-id="element-1630" data-mp="1kax6vt2vrrtm">
          <div className="min-h-[176px] flex-1 p-5" data-id="element-1631" data-mp="1oda05i0fxx9i">
            {loading ? <p className="flex items-center gap-2 text-ink-2" data-id="element-1632" data-mp="1t8pd87qmjp4v">
                <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1633" data-mp="1y9n3l7lsfphg" /> Translating…
              </p> : result && !result.supported ? <p className="text-[15px] text-ink-2" data-id="element-1634" data-mp="34p7aga21pvsk">
                This prototype's dictionary covers English ↔ Spanish. Try swapping to one of those.
              </p> : result ? <AnimatePresence mode="wait" data-id="element-1635" data-mp="3usuc2s5boxbk">
                <motion.div key={result.text} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={spring.smooth} data-id="element-1636" data-mp="3qriwzgk2ruhm">
                  <p className="text-2xl font-bold tracking-[-0.01em] text-ink" lang={to} data-id="element-1637" data-mp="3kyn3p8i7bvc">
                    {result.text}
                  </p>
                  {result.tokens.length > 1 && course && <div className="mt-5" data-id="element-1638" data-mp="de7574o97rpe">
                      <p className="text-xs font-semibold text-ink-2" data-id="element-1639" data-mp="rcdtpu7bcfce">Save single words</p>
                      <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-1640" data-mp="md0zgoe445zs">
                        {result.tokens.filter(t => t.known).map((t, i) => {
                    const pair = termAndMeaning(t.source, t.target);
                    const saved = isSaved(pair.term, course.id);
                    return <li key={`${t.target}-${i}`} data-id="element-1641" data-mp="18kwxxb21sat9" data-mpi={i}>
                                <motion.button type="button" whileTap={{
                        scale: 0.95
                      }} transition={spring.snappy} onClick={() => saveToken(t.source, t.target)} disabled={saved} className={`focus-ring flex items-center gap-1 rounded-lg border-2 border-line px-2 py-1 text-sm font-semibold ${saved ? "bg-good-tint text-ink" : "bg-surface text-ink hover:bg-accent hover:text-night"}`} title={pair.translation} data-id="element-1642" data-mp="18keqdi9xqa61">
                                  {saved && <BookmarkCheckIcon className="h-3.5 w-3.5" data-id="element-1643" data-mp="2aatk2qglzkh5" />}
                                  {pair.term}
                                </motion.button>
                              </li>;
                  })}
                      </ul>
                    </div>}
                </motion.div>
              </AnimatePresence> : <div data-id="element-1644" data-mp="2bbr1v9vumkho">
                <p className="text-[15px] text-ink-2" data-id="element-1645" data-mp="2baz906tmz0v3">Try one of these:</p>
                <ul className="mt-3 flex flex-wrap gap-2" data-id="element-1646" data-mp="2z1bqo9eoobyv">
                  {suggestions.map(s => <li key={s} data-id="element-1647" data-mp="33y25yuinz3j8">
                      <button type="button" onClick={() => {
                  go("en", "es");
                  setInput(s);
                }} className="focus-ring rounded-lg border-2 border-line bg-surface px-2.5 py-1 text-sm font-semibold text-ink hover:bg-accent hover:text-night" data-id="element-1648" data-mp="33xagm1fnu21e">
                        {s}
                      </button>
                    </li>)}
                </ul>
              </div>}
          </div>
          <div className="flex items-center justify-between gap-3 border-t-3 border-line px-4 py-3" data-id="element-1649" data-mp="162wmb0q78o9v">
            <div className="flex items-center gap-2" data-id="element-1650" data-mp="1kzxhxfbih3dm">
              <SpeakButton text={result?.text || " "} lang={getLanguage(to).speech} data-id="element-1651" data-mp="1g12rbr7ayaiy" />
              <button type="button" aria-label="Copy translation" disabled={!result?.text} onClick={() => {
              navigator.clipboard?.writeText(result?.text ?? "");
              toast("Copied");
            }} className="focus-ring grid h-10 w-10 place-items-center rounded-xl border-3 border-line bg-surface text-ink shadow-brutal-sm disabled:opacity-50" data-id="element-1652" data-mp="sbrw0p8q18c7">
                <CopyIcon className="h-4 w-4" data-id="element-1653" data-mp="9jfmnijnh6sk" />
              </button>
            </div>
            <button type="button" onClick={commit} disabled={!result?.text || !result.supported} className="focus-ring rounded-xl border-3 border-line bg-accent px-4 py-2 text-sm font-bold text-night shadow-brutal-sm disabled:opacity-50" data-id="element-1654" data-mp="4m71ra74aonm">
              {remember ? "Translate & remember" : "Done"}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-card border-3 border-line bg-surface p-4 md:flex-row md:items-center md:justify-between" data-id="element-1655" data-mp="bbqfsrptubwy">
        <div className="flex items-start gap-3" data-id="element-1656" data-mp="fe3i165d7roz">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-3 border-line bg-brand text-white" data-id="element-1657" data-mp="kcettmf80c9u">
            <BrainIcon className="h-5 w-5" data-id="element-1658" data-mp="pbrqeyvw8xpm" />
          </span>
          <div data-id="element-1659" data-mp="p957y47gsxsm">
            <Toggle checked={remember} onChange={setRemember} label="Remember what I translate" data-id="element-1660" data-mp="1vru0jhx8eii5" />
            <p className="mt-1 text-sm text-ink-2" data-id="element-1661" data-mp="20qojqkz4292s">
              {course ? `Saves into ${courseTitle(course)} · ${translatorWords.length} remembered, ${usedInLessons} already used in lessons.` : `No ${getLanguage(to).name} course yet — lookups won't be saved.`}{" "}
              <Link to={wordsPath(course)} className="font-semibold text-ink underline underline-offset-4" data-id="element-1662" data-mp="3bimhyk8vq3xt">
                See My Words
              </Link>
            </p>
          </div>
        </div>
      </div>

      <section aria-labelledby="recent-title" className="mt-10" data-id="element-1663" data-mp="32ir2424dpkrg">
        <h2 id="recent-title" className="text-lg font-bold tracking-[-0.01em]" data-id="element-1664" data-mp="32fv3ebkpis1y">
          Recent lookups
        </h2>
        <ul className="mt-3 divide-y-2 divide-line/10 rounded-card border-3 border-line bg-surface" data-id="element-1665" data-mp="2sjs2f91lczgl">
          {recent.map(r => {
          const c = courses.find(x => x.target === r.to && x.base === r.from || x.target === r.from && x.base === r.to);
          const term = c && c.target === r.to ? r.result : r.source;
          const saved = c ? isSaved(term, c.id) : false;
          const w = c ? words.find(x => x.courseId === c.id && x.term.toLowerCase() === term.toLowerCase()) : undefined;
          return <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3" data-id="element-1666" data-mp="ys1t4sa3dt3o">
                <span className="text-[15px] text-ink-2" data-id="element-1667" data-mp="jvaoo4lk9r77">{r.source}</span>
                <span className="text-ink-2" aria-hidden="true" data-id="element-1668" data-mp="ounkvfyboamg">→</span>
                <span className="text-[15px] font-bold text-ink" data-id="element-1669" data-mp="b3sau0c37nha">{r.result}</span>
                <span className="ml-auto flex gap-1.5" data-id="element-1670" data-mp="b1paamdb6byb">
                  {saved && <Tag tone="good" data-id="element-1671" data-mp="fvackqlxps3b">Saved</Tag>}
                  {w && w.usedIn > 0 && <Tag tone="tint" data-id="element-1672" data-mp="164fp9akkdyl">Used in {w.usedIn} lessons</Tag>}
                </span>
              </li>;
        })}
        </ul>
      </section>
    </div>;
}